import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { format } from 'date-fns';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';

interface ShiftData {
  asset_base: string;
  shift_label: string;
  shift_start: string;
  shift_end: string;
  distance_travelled_km: number;
}

interface Props {
  shiftWiseDistanceTravelled: ShiftData[];
}

const COLORS = {
  DAY: '#6366f1', // Indigo-500
  NIGHT: '#10b981', // Emerald-500
  GRID: '#e5e7eb', // Gray-200
  TEXT: '#ffffff', // white
  BACKGROUND: '#f9fafb', // Gray-50
};

const ShiftDistanceChart = ({ shiftWiseDistanceTravelled }: Props) => {
  const enableDarkTheme = useAppSelector(
    (state) => state.authReducer.customer?.theme === 'dark'
  );

  // Get unique assets from data
  const assets = useMemo(
    () =>
      Array.from(
        new Set(shiftWiseDistanceTravelled.map((item) => item.asset_base))
      ),
    [shiftWiseDistanceTravelled]
  );

  // State for selected asset with default as first asset
  const [selectedAsset, setSelectedAsset] = useState<string>('LHD882B'); // TODO: update this later on...

  // Filter and transform data for selected asset
  const transformData = () => {
    const filteredData = shiftWiseDistanceTravelled.filter(
      (item) => item.asset_base === selectedAsset
    );

    const groupedData: Record<string, any> = {};

    filteredData.forEach((item) => {
      const date = format(new Date(item.shift_start), 'MMM dd');
      const key = date; // Now only using date as key since we're filtering by asset

      if (!groupedData[key]) {
        groupedData[key] = {
          date,
          name: date,
        };
      }

      groupedData[key][`${item.shift_label}_shift`] =
        item.distance_travelled_km;
    });

    return Object.values(groupedData);
  };

  const chartData = transformData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold text-gray-800">
            {selectedAsset} - {label}
          </p>
          <p className="text-sm text-indigo-500">
            Day Shift:{' '}
            <span className="font-medium">{payload[0].value} km</span>
          </p>
          <p className="text-sm text-emerald-500">
            Night Shift:{' '}
            <span className="font-medium">{payload[1].value} km</span>
          </p>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    if (assets.length > 0) {
      setSelectedAsset(assets[0]);
    }
  }, [assets]);

  return (
    <div
      className="rounded-xl shadow-sm p-4 border"
      style={{
        backgroundColor: enableDarkTheme
          ? ThemePalette.dark.contentBackground
          : ThemePalette.light.contentBackground,
        borderColor: enableDarkTheme
          ? ThemePalette.dark.cartesianGrid
          : ThemePalette.light.cartesianGrid,
      }}
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h3
          className="text-lg font-semibold"
          style={{
            color: enableDarkTheme ? '#fff' : '#092b63',
          }}
        >
          Shift-wise Distance Traveled
        </h3>

        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <FormControl
              size="small"
              fullWidth
              variant="outlined"
              sx={{ minWidth: 140 }}
            >
              <InputLabel
                id="asset-filter"
                sx={{
                  color: enableDarkTheme ? '#fff' : '#000',
                  '&.Mui-focused': { color: enableDarkTheme ? '#fff' : '#000' },
                }}
              >
                Select Asset
              </InputLabel>
              <Select
                size="small"
                id="asset-filter"
                labelId="asset-filter"
                label="Select Asset"
                value={selectedAsset}
                onChange={(e) => {
                  setSelectedAsset(e.target.value);
                }}
                sx={{
                  color: enableDarkTheme ? '#fff' : '#000',
                  backgroundColor: enableDarkTheme
                    ? ThemePalette.dark.contentBackground
                    : ThemePalette.light.contentBackground,
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme
                      ? ThemePalette.dark.cartesianGrid
                      : ThemePalette.light.cartesianGrid,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme ? '#6366f1' : '#888',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme ? '#6366f1' : '#888',
                  },
                  '.MuiSvgIcon-root': {
                    color: enableDarkTheme ? '#fff' : '#000',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: enableDarkTheme
                        ? ThemePalette.dark.contentBackground
                        : ThemePalette.light.contentBackground,
                      color: enableDarkTheme ? '#fff' : '#000',
                    },
                  },
                }}
              >
                {assets.map((asset) => (
                  <MenuItem
                    key={asset}
                    value={asset}
                    sx={{
                      color: enableDarkTheme ? '#fff' : '#000',
                      bgcolor: enableDarkTheme
                        ? ThemePalette.dark.contentBackground
                        : ThemePalette.light.contentBackground,
                    }}
                  >
                    {asset}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2"></div>
              <span className="text-sm text-gray-600">Day Shift</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-emerald-500 mr-2"></div>
              <span className="text-sm text-gray-600">Night Shift</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ width: '100%', height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 20,
              left: 20,
              bottom: 60,
            }}
            barGap={4}
            barCategoryGap={12}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={
                enableDarkTheme
                  ? ThemePalette.dark.cartesianGrid
                  : ThemePalette.light.cartesianGrid
              }
            />
            <XAxis
              dataKey="name"
              tick={{ fill: enableDarkTheme ? '#fff' : '#000' }}
              tickMargin={10}
              axisLine={{ stroke: enableDarkTheme ? '#fff' : '#000' }}
              tickLine={{ stroke: enableDarkTheme ? '#fff' : '#000' }}
            />
            <YAxis
              tick={{ fill: enableDarkTheme ? '#fff' : '#000' }}
              axisLine={{ stroke: enableDarkTheme ? '#fff' : '#000' }}
              tickLine={{ stroke: enableDarkTheme ? '#fff' : '#000' }}
              label={{
                value: 'Distance (km)',
                angle: -90,
                position: 'insideLeft',
                fill: enableDarkTheme ? '#fff' : '#000',
                style: { textAnchor: 'middle' },
                offset: -10,
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                paddingBottom: '10px',
                color: enableDarkTheme ? '#fff' : '#000',
              }}
            />
            <Bar
              dataKey="D_shift"
              name="Day Shift"
              radius={[4, 4, 0, 0]}
              fill={COLORS.DAY}
            >
              {chartData.map((entry, index) => (
                <Cell key={`day-${index}`} fill={COLORS.DAY} />
              ))}
              <LabelList
                dataKey="D_shift"
                position="top"
                formatter={(value: number) => (value > 0 ? `${value} km` : '')}
                fill={enableDarkTheme ? '#fff' : '#000'}
                fontSize={12}
              />
            </Bar>
            <Bar
              dataKey="N_shift"
              name="Night Shift"
              radius={[4, 4, 0, 0]}
              fill={COLORS.NIGHT}
            >
              {chartData.map((entry, index) => (
                <Cell key={`night-${index}`} fill={COLORS.NIGHT} />
              ))}
              <LabelList
                dataKey="N_shift"
                position="top"
                formatter={(value: number) => (value > 0 ? `${value} km` : '')}
                fill={enableDarkTheme ? '#fff' : '#000'}
                fontSize={12}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ShiftDistanceChart;
